import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { intlLocaleFromI18n } from "../../../helpers/money";
import {
  formatCircuitAutoResumeCountdown,
  hasCircuitAutoResumeEta,
} from "../../../helpers/circuitAutoResumeEta";
import { useCircuitAutoResumeNowMs } from "../../../helpers/useCircuitAutoResumeNowMs";
import {
  formatSaRate,
  getSaSending,
  releaseSaStaleClaims,
  resumeSaSend,
  SaCampaignSend,
  SaQueueSnapshot,
} from "../../../helpers/super_admin_ops_helper";
import { shiftUtcDateIso } from "../../../helpers/utcDateIso";

const PER_PAGE = 25;

type SendingPreset = "all" | "canary" | "partial";

const statusColor = (status: string): string => {
  switch (status) {
    case "completed":
      return "success";
    case "sending":
    case "preparing":
    case "queued":
      return "info";
    case "failed":
      return "danger";
    case "cancelled":
    case "paused":
      return "warning";
    default:
      return "secondary";
  }
};

const pauseBadgeColor = (reason: string | null | undefined): string => {
  if (reason === "canary") return "danger";
  if (reason === "circuit" || reason === "platform") return "warning";
  return "secondary";
};

/**
 * Ops de disparos multi-tenant + snapshot de fila (LAC-042 · LAC-088 · LAC-142 · DOC 09 § 6.2).
 */
const SuperAdminSending = () => {
  const { t, i18n } = useTranslation();
  const locale = intlLocaleFromI18n(i18n.language);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPreset =
    searchParams.get("preset") === "canary"
      ? "canary"
      : searchParams.get("preset") === "partial" ||
          searchParams.get("active_partial_chain") === "1"
        ? "partial"
        : "all";
  const initialTenantRaw = searchParams.get("tenant_id") || "";
  const initialTenantId =
    initialTenantRaw && /^\d+$/.test(initialTenantRaw)
      ? Number(initialTenantRaw)
      : null;
  const initialDateRaw = searchParams.get("date") || "";
  const initialDate =
    /^\d{4}-\d{2}-\d{2}$/.test(initialDateRaw) ? initialDateRaw : "";
  const initialStatus = searchParams.get("status") || "";
  const initialPauseReason = searchParams.get("pause_reason") || "";
  const initialBounceKindRaw = searchParams.get("bounce_kind") || "";
  const initialBounceKind =
    initialBounceKindRaw === "hard" || initialBounceKindRaw === "soft"
      ? initialBounceKindRaw
      : "";
  const initialNeedsSa =
    searchParams.get("needs_sa_resume") === "1" ||
    searchParams.get("needs_sa_resume") === "true";
  const initialAutoEligible =
    searchParams.get("auto_resume_eligible") === "1" ||
    searchParams.get("auto_resume_eligible") === "true";

  const [items, setItems] = useState<SaCampaignSend[]>([]);
  const [queue, setQueue] = useState<SaQueueSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [preset, setPreset] = useState<SendingPreset>(
    initialNeedsSa || initialAutoEligible ? "all" : initialPreset
  );
  const [status, setStatus] = useState(
    initialNeedsSa || initialAutoEligible ? "paused" : initialStatus
  );
  const [pauseReason, setPauseReason] = useState(initialPauseReason);
  const [bounceKind, setBounceKind] = useState<"" | "hard" | "soft">(
    initialBounceKind
  );
  const [needsSaResume, setNeedsSaResume] = useState(initialNeedsSa);
  const [autoResumeEligible, setAutoResumeEligible] =
    useState(initialAutoEligible);
  const [resumingId, setResumingId] = useState<number | null>(null);
  const [releasingClaims, setReleasingClaims] = useState(false);
  const [tenantIdFilter, setTenantIdFilter] = useState<number | null>(
    initialTenantId
  );
  // LAC-142: dia UTC do spark (`?date=YYYY-MM-DD`).
  const [dateFilter, setDateFilter] = useState(initialDate);

  const listEtaActive = useMemo(
    () =>
      items.some((row) =>
        hasCircuitAutoResumeEta({
          status: row.status,
          pause_reason: row.pause_reason,
          circuit_auto_resume_eta_at: row.circuit_auto_resume_eta_at,
          needs_sa_resume: row.needs_sa_resume,
        })
      ),
    [items]
  );
  const etaNowMs = useCircuitAutoResumeNowMs(listEtaActive);

  document.title = t("sa.sending.documentTitle");

  const formatDateFilterLabel = (iso: string): string => {
    try {
      return new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(`${iso}T12:00:00Z`));
    } catch {
      return iso;
    }
  };

  const syncUrl = useCallback(
    (overrides: {
      preset?: SendingPreset;
      tenantId?: number | null;
      status?: string;
      pauseReason?: string;
      needsSa?: boolean;
      bounceKind?: "" | "hard" | "soft";
      autoEligible?: boolean;
      date?: string;
    } = {}) => {
      const nextPreset = overrides.preset ?? preset;
      const nextTenantId =
        overrides.tenantId !== undefined ? overrides.tenantId : tenantIdFilter;
      const nextStatus = overrides.status !== undefined ? overrides.status : status;
      const nextPauseReason =
        overrides.pauseReason !== undefined
          ? overrides.pauseReason
          : pauseReason;
      const nextNeedsSa =
        overrides.needsSa !== undefined ? overrides.needsSa : needsSaResume;
      const nextBounceKind =
        overrides.bounceKind !== undefined ? overrides.bounceKind : bounceKind;
      const nextAutoEligible =
        overrides.autoEligible !== undefined
          ? overrides.autoEligible
          : autoResumeEligible;
      const nextDate =
        overrides.date !== undefined ? overrides.date : dateFilter;

      const next = new URLSearchParams();
      if (nextPreset === "canary") next.set("preset", "canary");
      if (nextPreset === "partial") {
        next.set("preset", "partial");
        next.set("active_partial_chain", "1");
      }
      if (nextTenantId != null) next.set("tenant_id", String(nextTenantId));
      if (nextDate) next.set("date", nextDate);
      if (nextBounceKind) next.set("bounce_kind", nextBounceKind);
      if (nextNeedsSa) {
        next.set("needs_sa_resume", "1");
      } else if (nextAutoEligible) {
        next.set("auto_resume_eligible", "1");
      } else if (nextPreset !== "canary" && nextPreset !== "partial") {
        if (nextStatus) next.set("status", nextStatus);
        if (nextPauseReason) next.set("pause_reason", nextPauseReason);
      }
      setSearchParams(next, { replace: true });
    },
    [
      setSearchParams,
      preset,
      tenantIdFilter,
      status,
      pauseReason,
      needsSaResume,
      bounceKind,
      autoResumeEligible,
      dateFilter,
    ]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSaSending({
        page,
        per_page: PER_PAGE,
        status: preset === "canary" ? "paused" : status || undefined,
        pause_reason:
          preset === "canary" ? "canary" : pauseReason || undefined,
        bounce_kind: bounceKind || undefined,
        needs_sa_resume: needsSaResume ? 1 : undefined,
        auto_resume_eligible: autoResumeEligible ? 1 : undefined,
        active_partial_chain: preset === "partial" ? 1 : undefined,
        tenant_id: tenantIdFilter ?? undefined,
        date: dateFilter || undefined,
      });
      if (!res?.success) {
        setError(t("sa.sending.error.load"));
        setItems([]);
        setQueue(null);
        return;
      }
      setItems(res.data || []);
      setQueue(res.queue || null);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: any) {
      setError(e?.message || t("sa.sending.error.load"));
      setItems([]);
      setQueue(null);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    status,
    pauseReason,
    bounceKind,
    needsSaResume,
    autoResumeEligible,
    preset,
    tenantIdFilter,
    dateFilter,
    t,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const formatQueueCount = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(Number(value))) {
      return "—";
    }
    return Number(value).toLocaleString(locale);
  };

  const pauseLabel = (reason: string | null | undefined): string => {
    if (!reason) return "";
    return t(`sa.sending.pauseReason.${reason}`, { defaultValue: reason });
  };

  const handleResumeOne = async (id: number) => {
    setResumingId(id);
    try {
      const res = await resumeSaSend(id);
      toast.success(
        res?.message || t("sa.sending.resumeSuccess"),
        { autoClose: 2500 }
      );
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("sa.sending.resumeError"), {
        autoClose: 4000,
      });
    } finally {
      setResumingId(null);
    }
  };

  const handleReleaseStaleClaims = async (dryRun = false) => {
    setReleasingClaims(true);
    try {
      const res = await releaseSaStaleClaims({ dry_run: dryRun });
      const data = res.data;
      const msg =
        res.message ||
        (dryRun
          ? t("sa.sending.releaseStaleClaimsDry", {
              api: data?.api_messages ?? 0,
              recipients: data?.recipients ?? 0,
            })
          : t("sa.sending.releaseStaleClaimsOk", {
              api: data?.api_messages ?? 0,
              recipients: data?.recipients ?? 0,
            }));
      toast.success(msg, { autoClose: 3500 });
      if (!dryRun) await load();
    } catch (e: any) {
      toast.error(e?.message || t("sa.sending.error.load"), {
        autoClose: 4000,
      });
    } finally {
      setReleasingClaims(false);
    }
  };

  const sendP0Pending = queue?.send_p0_pending;
  const sendP0Danger =
    typeof sendP0Pending === "number" &&
    sendP0Pending > 0 &&
    queue?.worker_ok === false;

  return (
    <div className="page-content">
      <ToastContainer />
      <Container fluid>
        <BreadCrumb
          title={t("sa.sending.title")}
          pageTitle={t("sa.menu.sending")}
        />

        {error && (
          <Alert color="danger" className="d-flex justify-content-between gap-2">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={() => load()}>
              {t("sa.sending.retry")}
            </Button>
          </Alert>
        )}

        {tenantIdFilter != null || dateFilter ? (
          <Alert
            color="info"
            className="d-flex flex-wrap gap-2 justify-content-between align-items-center py-2"
          >
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {dateFilter ? (
                <ButtonGroup size="sm">
                  <Button
                    color="light"
                    onClick={() => {
                      const prev = shiftUtcDateIso(dateFilter, -1);
                      setDateFilter(prev);
                      setPage(1);
                      syncUrl({ date: prev });
                    }}
                    aria-label={t("sa.sending.prevDay")}
                    title={t("sa.sending.prevDay")}
                  >
                    ‹
                  </Button>
                  <Button
                    color="light"
                    onClick={() => {
                      const next = shiftUtcDateIso(dateFilter, 1);
                      setDateFilter(next);
                      setPage(1);
                      syncUrl({ date: next });
                    }}
                    aria-label={t("sa.sending.nextDay")}
                    title={t("sa.sending.nextDay")}
                  >
                    ›
                  </Button>
                </ButtonGroup>
              ) : null}
              <span>
                {tenantIdFilter != null && dateFilter
                  ? t("sa.sending.tenantDateFilterBanner", {
                      id: tenantIdFilter,
                      date: formatDateFilterLabel(dateFilter),
                    })
                  : tenantIdFilter != null
                    ? t("sa.sending.tenantFilterBanner", {
                        id: tenantIdFilter,
                      })
                    : t("sa.sending.dateFilterBanner", {
                        date: formatDateFilterLabel(dateFilter),
                      })}
              </span>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {tenantIdFilter != null ? (
                <Button
                  color="light"
                  size="sm"
                  tag={Link}
                  to={
                    dateFilter
                      ? `/super-admin/tenants/${tenantIdFilter}?spark_date=${encodeURIComponent(dateFilter)}`
                      : `/super-admin/tenants/${tenantIdFilter}`
                  }
                >
                  {t("sa.sending.backToTenant")}
                </Button>
              ) : null}
              {dateFilter ? (
                <Button
                  color="light"
                  size="sm"
                  onClick={() => {
                    setDateFilter("");
                    setPage(1);
                    syncUrl({ date: "" });
                  }}
                >
                  {t("sa.sending.clearDateFilter")}
                </Button>
              ) : null}
              {tenantIdFilter != null ? (
                <Button
                  color="light"
                  size="sm"
                  onClick={() => {
                    setTenantIdFilter(null);
                    setPage(1);
                    syncUrl({ tenantId: null });
                  }}
                >
                  {t("sa.sending.clearTenantFilter")}
                </Button>
              ) : null}
            </div>
          </Alert>
        ) : null}

        {queue ? (
          <Row className="g-3 mb-3">
            <Col lg={12}>
              {queue.worker_ok === false ? (
                <Alert color="danger" className="mb-3 py-2">
                  {t("sa.sending.workerDownAlert")}
                </Alert>
              ) : null}
              <Card
                className={`mb-0 ${
                  queue.worker_ok === false ? "border border-danger" : ""
                }`}
              >
                <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <h5 className="card-title mb-0">
                    {t("sa.sending.queueTitle")}
                  </h5>
                  <Button
                    color={sendP0Danger ? "danger" : "warning"}
                    size="sm"
                    disabled={releasingClaims}
                    onClick={() => void handleReleaseStaleClaims(false)}
                  >
                    {releasingClaims ? (
                      <Spinner size="sm" className="me-1" />
                    ) : null}
                    {t("sa.sending.releaseStaleClaims")}
                  </Button>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col sm={6} md={3}>
                      <p className="text-muted small mb-0">
                        {t("sa.sending.sendPending")}
                      </p>
                      <h4 className="mb-0">
                        {formatQueueCount(queue.send_pending)}
                      </h4>
                    </Col>
                    {sendP0Pending != null ? (
                      <Col sm={6} md={3}>
                        <p className="text-muted small mb-0">
                          {t("sa.sending.sendP0")}
                        </p>
                        <h4
                          className={`mb-0 ${
                            sendP0Danger ? "text-danger" : ""
                          }`}
                        >
                          {formatQueueCount(sendP0Pending)}
                        </h4>
                      </Col>
                    ) : null}
                    {queue.import_pending != null ? (
                      <Col sm={6} md={3}>
                        <p className="text-muted small mb-0">
                          {t("sa.sending.importPending")}
                        </p>
                        <h4 className="mb-0">
                          {formatQueueCount(queue.import_pending)}
                        </h4>
                      </Col>
                    ) : null}
                    {queue.worker_ok !== undefined &&
                    queue.worker_ok !== null ? (
                      <Col sm={6} md={3}>
                        <p className="text-muted small mb-0">
                          {t("sa.sending.workerStatus")}
                        </p>
                        <h4
                          className={`mb-0 ${
                            queue.worker_ok === false ? "text-danger" : ""
                          }`}
                        >
                          {queue.worker_ok === true
                            ? t("sa.sending.workerOk")
                            : t("sa.sending.workerDown")}
                        </h4>
                      </Col>
                    ) : null}
                    <Col sm={6} md={3}>
                      <p className="text-muted small mb-0">
                        {t("sa.sending.failedJobs")}
                      </p>
                      <h4 className="mb-0">
                        {formatQueueCount(queue.failed_jobs)}
                      </h4>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : null}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <h5 className="card-title mb-0 me-2">
                    {t("sa.sending.tableTitle")}
                  </h5>
                  <ButtonGroup size="sm">
                    <Button
                      color={
                        preset === "all" && !needsSaResume && !autoResumeEligible
                          ? "primary"
                          : "light"
                      }
                      onClick={() => {
                        setPreset("all");
                        setNeedsSaResume(false);
                        setAutoResumeEligible(false);
                        setPage(1);
                        syncUrl({
                          preset: "all",
                          needsSa: false,
                          autoEligible: false,
                        });
                      }}
                    >
                      {t("sa.sending.preset.all")}
                    </Button>
                    <Button
                      color={autoResumeEligible ? "warning" : "light"}
                      onClick={() => {
                        setPreset("all");
                        setNeedsSaResume(false);
                        setAutoResumeEligible(true);
                        setStatus("paused");
                        setPauseReason("");
                        setPage(1);
                        syncUrl({
                          preset: "all",
                          status: "paused",
                          pauseReason: "",
                          needsSa: false,
                          autoEligible: true,
                        });
                      }}
                    >
                      {t("sa.sending.preset.autoResume")}
                    </Button>
                    <Button
                      color={needsSaResume ? "danger" : "light"}
                      onClick={() => {
                        setPreset("all");
                        setNeedsSaResume(true);
                        setAutoResumeEligible(false);
                        setStatus("paused");
                        setPauseReason("");
                        setPage(1);
                        syncUrl({
                          preset: "all",
                          status: "paused",
                          pauseReason: "",
                          needsSa: true,
                          autoEligible: false,
                        });
                      }}
                    >
                      {t("sa.sending.preset.needsSa")}
                    </Button>
                    <Button
                      color={preset === "canary" ? "danger" : "light"}
                      onClick={() => {
                        setPreset("canary");
                        setNeedsSaResume(false);
                        setAutoResumeEligible(false);
                        setStatus("");
                        setPauseReason("");
                        setPage(1);
                        syncUrl({
                          preset: "canary",
                          status: "",
                          pauseReason: "",
                          needsSa: false,
                          autoEligible: false,
                        });
                      }}
                    >
                      {t("sa.sending.preset.canary")}
                    </Button>
                    <Button
                      color={preset === "partial" ? "primary" : "light"}
                      onClick={() => {
                        setPreset("partial");
                        setNeedsSaResume(false);
                        setAutoResumeEligible(false);
                        setStatus("");
                        setPauseReason("");
                        setPage(1);
                        syncUrl({
                          preset: "partial",
                          status: "",
                          pauseReason: "",
                          needsSa: false,
                          autoEligible: false,
                        });
                      }}
                    >
                      {t("sa.sending.preset.partial")}
                    </Button>
                  </ButtonGroup>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Input
                    type="date"
                    bsSize="sm"
                    style={{ maxWidth: 160 }}
                    value={dateFilter}
                    onChange={(e) => {
                      const next = e.target.value || "";
                      setDateFilter(next);
                      setPage(1);
                      syncUrl({ date: next });
                    }}
                    aria-label={t("sa.sending.filter.date")}
                  />
                  {preset === "all" && !needsSaResume && !autoResumeEligible ? (
                    <>
                      <Input
                        type="select"
                        bsSize="sm"
                        style={{ maxWidth: 200 }}
                        value={status}
                        onChange={(e) => {
                          const nextStatus = e.target.value;
                          setStatus(nextStatus);
                          setPage(1);
                          syncUrl({
                            preset: "all",
                            status: nextStatus,
                            needsSa: false,
                          });
                        }}
                        aria-label={t("sa.sending.filter.status")}
                      >
                        <option value="">
                          {t("sa.sending.filter.statusAll")}
                        </option>
                        <option value="queued">
                          {t("sending.status.queued")}
                        </option>
                        <option value="preparing">
                          {t("sending.status.preparing")}
                        </option>
                        <option value="sending">
                          {t("sending.status.sending")}
                        </option>
                        <option value="completed">
                          {t("sending.status.completed")}
                        </option>
                        <option value="failed">
                          {t("sending.status.failed")}
                        </option>
                        <option value="cancelled">
                          {t("sending.status.cancelled")}
                        </option>
                        <option value="paused">
                          {t("sending.status.paused")}
                        </option>
                      </Input>
                      <Input
                        type="select"
                        bsSize="sm"
                        style={{ maxWidth: 180 }}
                        value={bounceKind}
                        onChange={(e) => {
                          const next =
                            e.target.value === "hard" || e.target.value === "soft"
                              ? e.target.value
                              : "";
                          setBounceKind(next);
                          setPage(1);
                          syncUrl({
                            preset: "all",
                            bounceKind: next,
                            needsSa: false,
                          });
                        }}
                        aria-label={t("sa.sending.filter.bounceKind")}
                      >
                        <option value="">
                          {t("sa.sending.filter.bounceKindAll")}
                        </option>
                        <option value="hard">
                          {t("sa.sending.filter.bounceKindHard")}
                        </option>
                        <option value="soft">
                          {t("sa.sending.filter.bounceKindSoft")}
                        </option>
                      </Input>
                    </>
                  ) : (
                    <Input
                      type="select"
                      bsSize="sm"
                      style={{ maxWidth: 180 }}
                      value={bounceKind}
                      onChange={(e) => {
                        const next =
                          e.target.value === "hard" || e.target.value === "soft"
                            ? e.target.value
                            : "";
                        setBounceKind(next);
                        setPage(1);
                        syncUrl({ bounceKind: next });
                      }}
                      aria-label={t("sa.sending.filter.bounceKind")}
                    >
                      <option value="">
                        {t("sa.sending.filter.bounceKindAll")}
                      </option>
                      <option value="hard">
                        {t("sa.sending.filter.bounceKindHard")}
                      </option>
                      <option value="soft">
                        {t("sa.sending.filter.bounceKindSoft")}
                      </option>
                    </Input>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">
                      {dateFilter
                        ? t("sa.sending.emptyDate", {
                            date: formatDateFilterLabel(dateFilter),
                          })
                        : needsSaResume
                          ? t("sa.sending.emptyNeedsSa")
                          : preset === "canary"
                            ? t("sa.sending.emptyCanary")
                            : preset === "partial"
                              ? t("sa.sending.emptyPartial")
                              : t("sa.sending.empty")}
                    </p>
                    {dateFilter ? (
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        onClick={() => {
                          setDateFilter("");
                          setPage(1);
                          syncUrl({ date: "" });
                        }}
                      >
                        {t("sa.sending.viewAllDays")}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <p className="text-muted small mb-2">
                      {t("sa.sending.totalCount", { count: total })}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("sa.sending.table.tenant")}</th>
                            <th>{t("sa.sending.table.campaign")}</th>
                            <th>{t("sa.sending.table.status")}</th>
                            <th>{t("sa.sending.table.pauseReason")}</th>
                            <th>{t("sa.sending.table.sent")}</th>
                            <th>{t("sa.sending.table.bounceRate")}</th>
                            <th>{t("sa.sending.table.scheduled")}</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((row) => (
                            <tr key={row.id}>
                              <td>
                                {row.tenant ? (
                                  <Link
                                    to={`/super-admin/tenants/${row.tenant.id}?highlight=ramp`}
                                  >
                                    {row.tenant.name}
                                  </Link>
                                ) : (
                                  `#${row.tenant_id}`
                                )}
                                {row.is_test ? (
                                  <Badge
                                    color="secondary"
                                    className="ms-1"
                                    pill
                                  >
                                    {t("sending.badge.test")}
                                  </Badge>
                                ) : null}
                              </td>
                              <td>
                                <div className="fw-medium">
                                  {row.campaign?.name || "—"}
                                </div>
                                <div className="text-muted small">
                                  {row.subject}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1 align-items-center">
                                  <Badge color={statusColor(row.status)}>
                                    {t(`sending.status.${row.status}`, {
                                      defaultValue: row.status,
                                    })}
                                  </Badge>
                                  {row.waiting_for_capacity ? (
                                    <Badge color="warning" pill>
                                      {t("sa.sending.badge.waitingCapacity")}
                                    </Badge>
                                  ) : null}
                                  {row.priority_band === "p1" ? (
                                    <Badge color="info" pill>
                                      {t("sa.sending.badge.p1")}
                                    </Badge>
                                  ) : null}
                                  {row.priority_band === "p2" ? (
                                    <Badge color="secondary" pill>
                                      {t("sa.sending.badge.p2")}
                                    </Badge>
                                  ) : null}
                                  {row.partial_sample ||
                                  (row.partial_day_index ?? 0) > 0 ||
                                  (row.continuation_of_send_id ?? 0) > 0 ? (
                                    <Badge color="info" pill>
                                      {t("sa.sending.badge.partial", {
                                        day: row.partial_day_index || "—",
                                      })}
                                    </Badge>
                                  ) : null}
                                  {row.partial_chain_capped ? (
                                    <Badge color="warning" pill>
                                      {t("sa.sending.badge.partialCapped")}
                                    </Badge>
                                  ) : null}
                                </div>
                              </td>
                              <td>
                                {row.pause_reason ? (
                                  <div className="d-flex flex-column gap-1">
                                    <div className="d-flex flex-wrap gap-1">
                                      <Badge
                                        color={pauseBadgeColor(row.pause_reason)}
                                      >
                                        {pauseLabel(row.pause_reason)}
                                      </Badge>
                                      {row.needs_sa_resume ? (
                                        <Badge color="danger">
                                          {t("sa.sending.badge.needsSaResume")}
                                        </Badge>
                                      ) : hasCircuitAutoResumeEta({
                                          status: row.status,
                                          pause_reason: row.pause_reason,
                                          circuit_auto_resume_eta_at:
                                            row.circuit_auto_resume_eta_at,
                                          needs_sa_resume: row.needs_sa_resume,
                                        }) ? (
                                        <Badge color="warning">
                                          {t("sa.sending.badge.mayAutoResume")}
                                        </Badge>
                                      ) : null}
                                    </div>
                                    {hasCircuitAutoResumeEta({
                                      status: row.status,
                                      pause_reason: row.pause_reason,
                                      circuit_auto_resume_eta_at:
                                        row.circuit_auto_resume_eta_at,
                                      needs_sa_resume: row.needs_sa_resume,
                                    }) ? (
                                      <span className="small text-muted">
                                        {formatCircuitAutoResumeCountdown(
                                          row.circuit_auto_resume_eta_at,
                                          row.needs_sa_resume,
                                          etaNowMs,
                                          t,
                                          "list"
                                        )}
                                      </span>
                                    ) : null}
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {row.sent.toLocaleString(locale)}/
                                {row.queued.toLocaleString(locale)}
                              </td>
                              <td>
                                {formatSaRate(row.bounce_rate, locale)}
                              </td>
                              <td className="small text-muted">
                                {row.scheduled_at
                                  ? new Date(row.scheduled_at).toLocaleString(
                                      locale
                                    )
                                  : "—"}
                              </td>
                              <td className="text-end">
                                <div className="d-flex flex-wrap justify-content-end gap-1">
                                  {row.needs_sa_resume ||
                                  (row.status === "paused" &&
                                    (row.pause_reason === "circuit" ||
                                      row.pause_reason === "platform")) ? (
                                    <Button
                                      color="warning"
                                      size="sm"
                                      disabled={resumingId === row.id}
                                      onClick={() => void handleResumeOne(row.id)}
                                    >
                                      {resumingId === row.id ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        t("sa.sending.resumeOne")
                                      )}
                                    </Button>
                                  ) : null}
                                  {row.tenant ? (
                                    <Link
                                      to={`/super-admin/tenants/${row.tenant.id}?highlight=ramp`}
                                      className="btn btn-sm btn-soft-primary"
                                    >
                                      {t("sa.sending.openTenant")}
                                    </Link>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {lastPage > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="text-muted small">
                          {t("sa.sending.pagination", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("sa.sending.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("sa.sending.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SuperAdminSending;
